import { defineComponent as _defineComponent } from 'vue'
import { unref as _unref, createElementVNode as _createElementVNode, withCtx as _withCtx, createVNode as _createVNode, openBlock as _openBlock, createBlock as _createBlock, createCommentVNode as _createCommentVNode, toDisplayString as _toDisplayString, resolveComponent as _resolveComponent, normalizeProps as _normalizeProps, guardReactiveProps as _guardReactiveProps, createTextVNode as _createTextVNode, normalizeStyle as _normalizeStyle } from "vue"

const _hoisted_1 = ["innerHTML"]
const _hoisted_2 = { class: "text-h6 q-my-md" }
const _hoisted_3 = { class: "text-body1" }

import { sanitize } from "dompurify"
import { useDialogPluginComponent } from "quasar"
import SfxBtn from "src/components/button/SfxBtn.vue"
import SfxImageUploader from "src/components/images/SfxImageUploader.vue"
import SfxTextareaTitled from "src/components/input/SfxTextareaTitled.vue"
import SfxIssueAdditionalRequirements from "src/components/issue/SfxIssueAdditionalRequirements.vue"
import SfxIssueBase from "src/components/issue/SfxIssueBase.vue"
import SfxIssueCorrectiveAction from "src/components/issue/SfxIssueCorrectiveAction.vue"
import SfxIssueTimeRequirements from "src/components/issue/SfxIssueTimeRequirements.vue"
import SfxTopNavigation from "src/components/navigation/SfxTopNavigation.vue"
import SfxPriorityLabel from "src/components/priority-label/SfxPriorityLabel.vue"
import SfxSelect from "src/components/select/SfxSelect.vue"
import SfxEntityReference from "src/components/special/SfxEntityReference.vue"
import SfxSeparator from "src/components/special/SfxSeparator.vue"
import { getChangesDB, getDB } from "src/db/databases"
import { useCacheStore } from "src/stores/cacheStore"
import { useUserStore } from "src/stores/userStore"
import { Log } from "src/util/classes/log"
import { Icons } from "src/util/enums/fontAwesomeIcons"
import { NotificationActionMessage } from "src/util/enums/notifications"
import { NavigationAction } from "src/util/enums/topNavigation"
import { uploadImages } from "src/util/functions/attachments"
import {
  confirmationDialog,
  issueListDialog,
  unsavedChangesDialog,
} from "src/util/functions/dialogs"
import {
  getIssueOnItemListData,
  getIssuePriorityListData,
  getWorkDomainWorkspaceListData,
} from "src/util/functions/entityData"
import { checkScan, verifyIdentifier } from "src/util/functions/identifier"
import { getHighestPriorityFromIssueList, getIssueCardData } from "src/util/functions/issues"
import { getItemData } from "src/util/functions/item"
import { numberToPriorityClass } from "src/util/functions/misc"
import { showErrorNotification, showSuccessNotification } from "src/util/functions/notifications"
import { filterInvalidWorkDomains } from "src/util/functions/performedWork"
import { getTemporalInfo } from "src/util/functions/time"
import { saveUnsubmittedAttachments } from "src/util/functions/unsubmittedData"
import { postIssuesOnItem, postIssuesOnItemAction } from "src/util/http/csi/issues"
import { AttachmentUpload } from "src/util/types/dataModels/attachment"
import { PayloadEntity } from "src/util/types/dataModels/genericObject"
import { IdentifierEntityTarget } from "src/util/types/dataModels/identifier"
import {
  Issue,
  IssueEntity,
  IssuesOnItem,
  IssuesOnItemAction,
} from "src/util/types/dataModels/issue"
import { IssuePriority } from "src/util/types/dataModels/issuePriority"
import { UnsubmittedWorkIssue } from "src/util/types/dataModels/unsubmittedWork"
import { WorkDomainWorkspace } from "src/util/types/dataModels/workDomain"
import { v4 as uuidv4 } from "uuid"
import { computed, onMounted, PropType, ref, shallowRef, watch } from "vue"
import { useI18n } from "vue-i18n"

//region Props and Emits


export default /*#__PURE__*/_defineComponent({
  __name: 'IssueCreateOverlay',
  props: {
  issueEntity: {
    type: Object as PropType<IssueEntity>,
    required: true,
  },

  unsubmittedWorkId: {
    type: String,
    default: undefined,
  },
},
  emits: [...useDialogPluginComponent.emits],
  setup(__props) {

const props = __props



//endregion

//region Variables

const { t } = useI18n()
const { dialogRef, onDialogCancel, onDialogHide, onDialogOK } = useDialogPluginComponent()
const cacheStore = useCacheStore()

const topNavigationContainer = ref<HTMLDivElement | null>(null)

const pageSubtitle = t("issueCreateOverlay.subtitle", {
  designation: sanitize(props.issueEntity.designation),
})
const existingIssuesLabel = t("issueCreateOverlay.entity_ref_label")
const workDomainLabel = t("issueCreateOverlay.work_domain_label")
const selectPriorityLabel = t("issueCreateOverlay.select_priority_label")
const issueDescriptionLabel = t("issueCreateOverlay.issue_description_label")
const correctionInfoLabel = t("issueCreateOverlay.correction_info")

const issuesOnEntity = shallowRef<IssuesOnItem[]>([])
const issueOnEntityTotal = ref<number | undefined>()
const issueOnEntityPriorityColor = ref<string>("")
const issueOnEntityPriorityLabel = ref<string>("")
const issueOnEntityTimeLabel = ref<string>("")

const workDomainModel = ref()
const workDomainOptions = ref<WorkDomainWorkspace[]>([])
const issuePrioritiesList = ref<IssuePriority[]>([])
const issuePrioritiesListFiltered = ref<IssuePriority[]>()
const selectPriorityModel = ref<IssuePriority | undefined>()
const issueTitle = ref<string | null>(null)
const issueDescription = ref<string | null>(null)
const uploadedImages = ref<AttachmentUpload[] | undefined>()

const correctiveAction = ref<string | undefined>()
const requiresIsolation = ref<boolean | undefined>()
const requiresShutdown = ref<boolean | undefined>()
const timeEstimate = ref<string | undefined>()

const correctiveActionEl = ref<typeof SfxIssueCorrectiveAction | undefined>()
const correctiveTimeRequirementsEl = ref<typeof SfxIssueTimeRequirements | undefined>()
const correctiveAdditionalRequirementsEl = ref<typeof SfxIssueAdditionalRequirements | undefined>()

// Set to true, since there are WD that don't require proof and we manually update on WD change.
const hasProofOfPresence = ref(true)
const identifierTarget = computed<IdentifierEntityTarget>(() => {
  return {
    entityType: "item",
    entityName: props.issueEntity.designation,
    entityId: props.issueEntity.id,
  }
})

const title = computed(() => issueTitle.value || t("issueCreateOverlay.title"))

const titleReference = computed(
  () => workDomainModel.value?.shorthand_name || t("issueCreateOverlay.title_reference_placeholder")
)

const titleClass = computed(() =>
  !issueTitle.value ? "sfx-top-navigation__title-placeholder" : undefined
)

const titleReferenceClass = computed(() =>
  !workDomainModel.value ? "sfx-top-navigation__title-placeholder" : undefined
)

const scrollAreaHeight = computed(() => {
  const topNavigationHeight = topNavigationContainer.value?.offsetHeight || 0
  return { height: `calc(100% - ${topNavigationHeight}px)` }
})

const disableSubmit = computed(
  () =>
    !(
      workDomainModel.value &&
      hasProofOfPresence.value &&
      issueTitle.value &&
      selectPriorityModel.value &&
      uploadedImages.value &&
      uploadedImages.value?.length > 0
    )
)

const unSavedChanges = computed(() =>
  Boolean(
    workDomainModel.value ||
      issueTitle.value ||
      selectPriorityModel.value ||
      (uploadedImages.value && uploadedImages.value.length > 0)
  )
)
//endregion

//region Lifecycle

onMounted(async () => {
  const issuePriorityDocuments = await getIssuePriorityListData()
  const issuePriorityData = issuePriorityDocuments.map((element) => element.toJSON())

  issuePrioritiesList.value = issuePriorityData.sort((a: IssuePriority, b: IssuePriority) => {
    return a.risk_factor - b.risk_factor
  })

  await assignValidWorkDomains()
  await getIssuesForEntity()
})

//endregion

//region Functions

async function assignValidWorkDomains() {
  const workDomainDocuments = await getWorkDomainWorkspaceListData()

  const item = await getItemData(props.issueEntity.id)

  const validWorkDomainOptions = await filterInvalidWorkDomains(
    workDomainDocuments,
    item,
    issuePrioritiesList.value
  )

  if (validWorkDomainOptions.length === 0) {
    return showErrorNotification(t("sfxErrorMsg.error_no_valid_work_domains"))
  }

  workDomainOptions.value = validWorkDomainOptions
}

async function handleUnsavedChanges(action: () => void): Promise<void> {
  if (unSavedChanges.value) {
    // Show confirmation dialog
    const userConfirmed = await unsavedChangesDialog()
    if (userConfirmed) {
      action()
    }
  } else {
    action()
  }
}

function showIssuesDialog() {
  issueListDialog("item", props.issueEntity, issuesOnEntity.value, unSavedChanges.value)
}

function onDialogClose() {
  handleUnsavedChanges(onDialogCancel)
}

// TODO: This has to be reworked
async function getIssuesForEntity() {
  // TODO: add check for getting data for items or areas
  const allIssuesOnEntity = await getIssueOnItemListData()

  const issues = allIssuesOnEntity.filter(
    (issue) => issue.actionable_item === props.issueEntity.id && issue.concluded_dt === null
  )

  if (issues.length === 0) return

  issuesOnEntity.value = issues.map((i) => i.toMutableJSON())
  issueOnEntityTotal.value = issues.length

  const latestIssue = issues.reduce((a, b) =>
    new Date(a.declared_dt || a.created_dt).getTime() >
    new Date(b.declared_dt || b.created_dt).getTime()
      ? a
      : b
  )

  const highestPriorityIssue = await getHighestPriorityFromIssueList(issues)
  if (highestPriorityIssue) {
    issueOnEntityPriorityColor.value = numberToPriorityClass(highestPriorityIssue.risk_factor)
    issueOnEntityPriorityLabel.value = highestPriorityIssue.label
  }

  const issueDateTime = new Date(latestIssue.declared_dt || latestIssue.created_dt).toJSON()

  const latestTemporalData = getTemporalInfo(issueDateTime, "objOverviewIssue")

  if (typeof latestTemporalData === "string") {
    issueOnEntityTimeLabel.value = latestTemporalData
  }
}

async function createIssue(): Promise<IssuesOnItem | undefined> {
  if (!issueTitle.value || !selectPriorityModel.value || !props.issueEntity) {
    const message = t("issueCreateOverlay.user_error_creating_issue")
    showErrorNotification(message)
  } else {
    const userStore = useUserStore()

    try {
      // TODO: Handle issue for area case
      const issue: PayloadEntity<IssuesOnItem> = {
        title: issueTitle.value,
        description: issueDescription.value,
        raised_by_user: userStore.id,
        concluded_by_user: null,
        work_domain: workDomainModel.value.id,
        priority: selectPriorityModel.value.id,
        actionable_item: props.issueEntity.id,
        declared_dt: new Date().toJSON(),
        concluded_dt: null,
        conclusion: null,
        superseded_by: null,
        corrective_work_order: null,
        raised_from: null,
        reference: null,
        attachments: [],
        main_image: null,
      }

      if (props.unsubmittedWorkId) {
        issue.attachments = (await saveUnsubmittedAttachments(uploadedImages.value)) || []
        await saveUnsubmittedIssueData(props.unsubmittedWorkId, issue)
      } else {
        const issueCreated = await postIssuesOnItem({}, issue)

        issueCreated.attachments =
          (await uploadImages("issue_item", issueCreated.id, uploadedImages.value)) || []

        const db = await getDB()
        await db.issues_on_item.insert(issueCreated)

        return issueCreated
      }
    } catch (error) {
      showErrorNotification(t("sfxErrorMsg.error_creating_issue"))
      Log.error({
        message: "An error occurred while trying to create the issue.",
        error,
      })
    }
  }
}

async function createActions(correctiveActionText: string, issueID: string) {
  try {
    const action: PayloadEntity<IssuesOnItemAction> = {
      text: correctiveActionText,
      time_estimate: timeEstimate.value || undefined,
      isolation_required: requiresIsolation.value || undefined,
      shutdown_required: requiresShutdown.value || undefined,
      issue: issueID,
    }
    const issueActionCreated = await postIssuesOnItemAction({}, action)

    if (issueActionCreated) {
      const db = await getDB()
      await db.issue_on_item_actions.insert(issueActionCreated)
    }
  } catch (error) {
    showErrorNotification(t("sfxErrorMsg.error_creating_corrective_action"))
    Log.error({
      message: "An error occurred while trying to create an action for the item.",
      error,
    })
  }
}

async function submitData() {
  const issueCreated = await createIssue()

  if (issueCreated) {
    if (correctiveAction.value) {
      await createActions(correctiveAction.value, issueCreated.id)
    }

    const issueCardData = await getIssueCardData(issueCreated)
    cacheStore.issueCard.set(issueCreated.id, issueCardData)

    showSuccessNotification(NotificationActionMessage.ISSUES_RAISED)
    onDialogOK()
  }
}

async function saveUnsubmittedIssueData(unsubmittedWorkId: string, issue: PayloadEntity<Issue>) {
  try {
    const db = await getChangesDB()
    const issueId = uuidv4()

    const issueToSend: UnsubmittedWorkIssue = {
      id: issueId,
      unsubmitted_work_id: unsubmittedWorkId,
      issue: issue,
    }
    if (correctiveAction.value) {
      issueToSend.action = {
        text: correctiveAction.value,
        time_estimate: timeEstimate.value || undefined,
        isolation_required: requiresIsolation.value || undefined,
        shutdown_required: requiresShutdown.value || undefined,
        issue: issueId,
      }
    }
    await db.unsubmitted_work_issue.insert(issueToSend)

    showSuccessNotification(t("issueCreateOverlay.work_related_issue_message"))
    onDialogHide()
  } catch (error) {
    Log.error({
      message: "An error occurred while saving Unsubmitted Issue data in changes_db",
      error,
    })
  }
}

async function clearCorrectiveInformation(correctiveActionHadData: boolean) {
  if (
    !correctiveActionHadData &&
    (timeEstimate.value || requiresIsolation.value || requiresShutdown.value)
  ) {
    const userConfirmed = await confirmationDialog({
      title: t("sfxIssues.dialog_title"),
      content: t("sfxIssues.dialog_content"),
      persistent: true,
    })

    if (!userConfirmed) {
      correctiveActionEl.value?.forceShow()
      return
    }
  }

  correctiveTimeRequirementsEl.value?.forceHide()
  correctiveAdditionalRequirementsEl.value?.forceHide()
}

//endregion

//region Setup Code

watch(
  () => workDomainModel.value,
  async () => {
    hasProofOfPresence.value = await checkScan(identifierTarget.value)
    selectPriorityModel.value = undefined
    issuePrioritiesListFiltered.value = issuePrioritiesList.value?.filter(
      (issuePriority) => issuePriority.work_domain === workDomainModel.value.id
    )
  }
)

//endregion

return (_ctx: any,_cache: any) => {
  const _component_q_item = _resolveComponent("q-item")!
  const _component_q_form = _resolveComponent("q-form")!
  const _component_q_scroll_area = _resolveComponent("q-scroll-area")!
  const _component_q_card = _resolveComponent("q-card")!
  const _component_q_dialog = _resolveComponent("q-dialog")!

  return (_openBlock(), _createBlock(_component_q_dialog, {
    ref_key: "dialogRef",
    ref: dialogRef,
    onHide: _unref(onDialogHide),
    "transition-show": "slide-up",
    "transition-hide": "slide-down",
    maximized: ""
  }, {
    default: _withCtx(() => [
      _createVNode(_component_q_card, { class: "q-dialog-plugin" }, {
        default: _withCtx(() => [
          _createElementVNode("div", {
            ref_key: "topNavigationContainer",
            ref: topNavigationContainer
          }, [
            _createVNode(SfxTopNavigation, {
              "navigation-action": _unref(NavigationAction).CLOSE,
              "hide-context-bar": "",
              onClose: onDialogClose,
              title: title.value,
              "title-reference": titleReference.value,
              "title-class": titleClass.value,
              "title-reference-class": titleReferenceClass.value
            }, {
              subtitle: _withCtx(() => [
                _createElementVNode("span", { innerHTML: _unref(pageSubtitle) }, null, 8, _hoisted_1)
              ]),
              _: 1
            }, 8, ["navigation-action", "title", "title-reference", "title-class", "title-reference-class"])
          ], 512),
          _createVNode(_component_q_scroll_area, {
            class: "q-px-lg full-width scrollbar-area",
            style: _normalizeStyle(scrollAreaHeight.value),
            visible: false
          }, {
            default: _withCtx(() => [
              (issueOnEntityTotal.value)
                ? (_openBlock(), _createBlock(SfxEntityReference, {
                    key: 0,
                    title: _unref(existingIssuesLabel),
                    "item-count": issueOnEntityTotal.value,
                    "time-information": issueOnEntityTimeLabel.value,
                    icon: _unref(Icons).ISSUE,
                    "icon-color": issueOnEntityPriorityColor.value,
                    "priority-label": issueOnEntityPriorityLabel.value,
                    clickable: "",
                    onClick: showIssuesDialog
                  }, null, 8, ["title", "item-count", "time-information", "icon", "icon-color", "priority-label"]))
                : _createCommentVNode("", true),
              _createElementVNode("div", _hoisted_2, _toDisplayString(_ctx.$t("issueCreateOverlay.issue")), 1),
              _createElementVNode("div", _hoisted_3, _toDisplayString(_ctx.$t("issueCreateOverlay.issue_more_info")), 1),
              _createVNode(_component_q_form, { onSubmit: submitData }, {
                default: _withCtx(() => [
                  _createVNode(SfxSelect, {
                    modelValue: workDomainModel.value,
                    "onUpdate:modelValue": _cache[0] || (_cache[0] = ($event: any) => ((workDomainModel).value = $event)),
                    label: _unref(workDomainLabel),
                    options: workDomainOptions.value,
                    "option-label": (opt) => opt.designation,
                    "option-value": (opt) => opt.id,
                    disable: workDomainOptions.value.length === 0,
                    class: "q-my-xs",
                    required: ""
                  }, null, 8, ["modelValue", "label", "options", "option-label", "option-value", "disable"]),
                  _createVNode(SfxSelect, {
                    modelValue: selectPriorityModel.value,
                    "onUpdate:modelValue": _cache[1] || (_cache[1] = ($event: any) => ((selectPriorityModel).value = $event)),
                    label: _unref(selectPriorityLabel),
                    options: issuePrioritiesListFiltered.value,
                    class: "q-mb-xs",
                    disable: !workDomainModel.value,
                    required: ""
                  }, {
                    option: _withCtx((scope) => [
                      _createVNode(_component_q_item, _normalizeProps(_guardReactiveProps(scope.itemProps)), {
                        default: _withCtx(() => [
                          _createVNode(SfxPriorityLabel, {
                            "priority-number": scope.opt.risk_factor,
                            label: scope.opt.label,
                            issue: ""
                          }, null, 8, ["priority-number", "label"])
                        ]),
                        _: 2
                      }, 1040)
                    ]),
                    "selected-item": _withCtx((scope) => [
                      _createVNode(SfxPriorityLabel, {
                        "priority-number": scope.opt.risk_factor,
                        label: scope.opt.label,
                        issue: ""
                      }, null, 8, ["priority-number", "label"])
                    ]),
                    _: 1
                  }, 8, ["modelValue", "label", "options", "disable"]),
                  _createVNode(SfxTextareaTitled, {
                    title: issueTitle.value,
                    "onUpdate:title": _cache[2] || (_cache[2] = ($event: any) => ((issueTitle).value = $event)),
                    description: issueDescription.value,
                    "onUpdate:description": _cache[3] || (_cache[3] = ($event: any) => ((issueDescription).value = $event)),
                    label: _unref(issueDescriptionLabel),
                    class: "q-mt-xs q-mb-xs"
                  }, null, 8, ["title", "description", "label"]),
                  _createVNode(SfxImageUploader, {
                    "uploaded-images": uploadedImages.value,
                    "onUpdate:uploadedImages": _cache[4] || (_cache[4] = ($event: any) => ((uploadedImages).value = $event)),
                    required: ""
                  }, null, 8, ["uploaded-images"]),
                  _createVNode(SfxSeparator, null, {
                    default: _withCtx(() => [
                      _createTextVNode(_toDisplayString(_unref(correctionInfoLabel)), 1)
                    ]),
                    _: 1
                  }),
                  _createVNode(SfxIssueCorrectiveAction, {
                    ref_key: "correctiveActionEl",
                    ref: correctiveActionEl,
                    modelValue: correctiveAction.value,
                    "onUpdate:modelValue": _cache[5] || (_cache[5] = ($event: any) => ((correctiveAction).value = $event)),
                    onHide: clearCorrectiveInformation
                  }, null, 8, ["modelValue"]),
                  _createVNode(SfxIssueTimeRequirements, {
                    ref_key: "correctiveTimeRequirementsEl",
                    ref: correctiveTimeRequirementsEl,
                    "time-estimate": timeEstimate.value,
                    "onUpdate:timeEstimate": _cache[6] || (_cache[6] = ($event: any) => ((timeEstimate).value = $event)),
                    disable: !correctiveAction.value
                  }, null, 8, ["time-estimate", "disable"]),
                  _createVNode(SfxIssueBase, {
                    title: "Material Requirements",
                    disable: ""
                  }),
                  _createVNode(SfxIssueAdditionalRequirements, {
                    ref_key: "correctiveAdditionalRequirementsEl",
                    ref: correctiveAdditionalRequirementsEl,
                    "isolation-checked": requiresIsolation.value,
                    "onUpdate:isolationChecked": _cache[7] || (_cache[7] = ($event: any) => ((requiresIsolation).value = $event)),
                    "shutdown-checked": requiresShutdown.value,
                    "onUpdate:shutdownChecked": _cache[8] || (_cache[8] = ($event: any) => ((requiresShutdown).value = $event)),
                    disable: !correctiveAction.value
                  }, null, 8, ["isolation-checked", "shutdown-checked", "disable"]),
                  (!hasProofOfPresence.value)
                    ? (_openBlock(), _createBlock(SfxBtn, {
                        key: 0,
                        label: _ctx.$t('sfxButtons.btn_proof_of_presence'),
                        class: "full-width q-mb-md",
                        onClick: _cache[9] || (_cache[9] = ($event: any) => (_unref(verifyIdentifier)(identifierTarget.value)))
                      }, null, 8, ["label"]))
                    : _createCommentVNode("", true),
                  _createVNode(SfxBtn, {
                    label: _ctx.$t('sfxButtons.btn_raise_issue'),
                    class: "full-width q-mb-md",
                    type: "submit",
                    disable: disableSubmit.value
                  }, null, 8, ["label", "disable"])
                ]),
                _: 1
              })
            ]),
            _: 1
          }, 8, ["style"])
        ]),
        _: 1
      })
    ]),
    _: 1
  }, 8, ["onHide"]))
}
}

})